import { useCallback, useEffect, useRef, useState } from "react";
import reportHealthService from "../../../../../../../../Service/OPSServices/StoreHealthServices/reportHealthService.js";

const POLL_MS = 3000;
const MAX_POLLS = 200; // ~10 minutes

/**
 * Runs a retry (report level or bulk), then polls /retry-status until the background job finishes.
 * `retrying` holds the "profileId|report" keys being retried, so their cells show "Retrying".
 */
export function useRetry(onDone) {
  const [retrying, setRetrying] = useState(() => new Set());
  const timers = useRef([]);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const run = useCallback(async (start, keys, label) => {
    setRetrying((prev) => new Set([...prev, ...keys]));
    const release = () =>
      setRetrying((prev) => {
        const n = new Set(prev);
        keys.forEach((k) => n.delete(k));
        return n;
      });
    let r;
    try {
      r = await start();
    } catch (e) {
      release();
      doneRef.current({ error: true, text: `${label} could not start: ${e.message}` });
      return;
    }
    if (!r.task_id) {
      release();
      doneRef.current({
        text: r.skipped ? `${label}: already being retried.` : r.message || "Nothing to retry.",
      });
      return;
    }
    let polls = 0;
    const tick = async () => {
      try {
        const t = await reportHealthService.retryStatus(r.task_id);
        if (!t.done && polls++ < MAX_POLLS) {
          timers.current.push(setTimeout(tick, POLL_MS));
          return;
        }
        release();
        if (t.state === "SUCCESS") {
          const res = t.result || {};
          const errors = (res.results || []).filter((x) => !x.ok);
          const first = errors[0] ? ` First error: ${errors[0].report}: ${errors[0].error}` : "";
          doneRef.current({
            error: !!errors.length && !res.retried,
            text:
              `${label}: ${res.retried || 0} sent to Amazon again${errors.length ? `, ${errors.length} failed.${first}` : "."}` +
              (r.skipped ? ` ${r.skipped} were already being retried.` : "") +
              (res.retried ? " They show as Waiting until the next save run." : ""),
          });
        } else if (t.state === "FAILURE") {
          doneRef.current({
            error: true,
            text: `${label} failed: ${t.error || "see the Celery log"}.`,
          });
        } else {
          doneRef.current({ text: `${label} is still running; check back in a few minutes.` });
        }
      } catch (e) {
        if (polls++ < MAX_POLLS) {
          timers.current.push(setTimeout(tick, POLL_MS * 2));
          return;
        }
        release();
        doneRef.current({
          error: true,
          text: `Lost track of ${label.toLowerCase()}: ${e.message}`,
        });
      }
    };
    tick();
  }, []);

  return { retrying, run };
}

export default useRetry;
